import { useState } from "react";
import {
  COACHES,
  COACHES_PAGE_STRINGS,
  type CoachRecord,
  type Lang,
} from "@/data/coaches";
import { CareersFooter, CareersNav } from "@/careers/CareersChrome";
import { startHref } from "@/lib/start-href";
import { BLOG_COPY } from "@/components/blog/blog-copy";
import { blogPostPath } from "@/lib/blog";

/** Статьи коучей на языке страницы: id коуча → [{slug, title}] (loader маршрута /coaches). */
export type CoachPosts = Record<string, { slug: string; title: string }[]>;

// Coaches page — list + inline-expanded profiles. Renders the same
// component for /coaches, /az/coaches, /en/coaches; the route file
// passes the locale. Brand tokens (Paper / Cream / Teal / Amber) and
// fonts (Source Serif / Nunito) follow CC_brief_coaches_section.md §4.

const P = {
  paper: "#FBFAF7",
  cream: "#F4F1EA",
  ink: "#0F2A38",
  inkMuted: "#2D4756",
  muted: "#6B8090",
  line: "#E3E7EB",
  teal: "#1E5A6B",
  tealDeep: "#143F4B",
  amber: "#F0A63A",
  amberInk: "#C9821F",
} as const;

const FONT = "'Nunito Sans', system-ui, sans-serif";
const SERIF = "'Source Serif 4', Georgia, serif";
const MONO = "'JetBrains Mono', ui-monospace, monospace";

function CoachCard({ coach, lang, posts }: { coach: CoachRecord; lang: Lang; posts?: { slug: string; title: string }[] }) {
  const [expanded, setExpanded] = useState(false);
  const s = COACHES_PAGE_STRINGS;

  return (
    <article
      id={`coach-${coach.id}`}
      style={{
        scrollMarginTop: 90,
        background: P.paper,
        border: `1px solid ${P.line}`,
        borderRadius: 18,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 1px 0 rgba(15, 42, 56, .02)",
      }}
    >
      {/* Portrait — fixed 3:4 aspect for grid evenness when multiple
          coaches land. object-fit: cover handles photos that aren't
          exactly cropped. */}
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "3 / 4",
          background: P.cream,
          overflow: "hidden",
        }}
      >
        <img
          src={coach.photo}
          alt={coach.photoAlt[lang]}
          loading="lazy"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center top",
          }}
        />
        {/* Amber accent dot — brand cue */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            width: 10,
            height: 10,
            borderRadius: 999,
            background: P.amber,
            boxShadow: "0 0 0 4px rgba(240, 166, 58, .18)",
          }}
        />
      </div>

      <div style={{ padding: "22px 24px 26px", display: "flex", flexDirection: "column", gap: 12 }}>
        <h3
          style={{
            margin: 0,
            fontFamily: SERIF,
            fontSize: 22,
            lineHeight: 1.2,
            color: P.tealDeep,
            fontWeight: 600,
            letterSpacing: "-.005em",
          }}
        >
          {coach.name[lang]}
        </h3>
        <div
          style={{
            fontSize: 13,
            color: P.muted,
            lineHeight: 1.45,
            fontWeight: 500,
          }}
        >
          {coach.role[lang]}
        </div>
        <p
          style={{
            margin: 0,
            fontSize: 14.5,
            color: P.inkMuted,
            lineHeight: 1.55,
          }}
        >
          {coach.tagline[lang]}
        </p>

        {/* Meta row: experience + languages */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "center",
            marginTop: 4,
          }}
        >
          <span
            style={{
              fontFamily: MONO,
              fontSize: 11,
              letterSpacing: ".04em",
              color: P.amberInk,
              background: "rgba(240, 166, 58, .12)",
              border: `1px solid rgba(240, 166, 58, .35)`,
              padding: "5px 10px",
              borderRadius: 999,
            }}
          >
            {coach.experienceBadge[lang]}
          </span>
          <span style={{ fontSize: 12, color: P.muted, marginLeft: "auto" }}>
            {s.languagesLabel[lang]}:
          </span>
          {coach.languages[lang].map((l) => (
            <span
              key={l}
              style={{
                fontFamily: MONO,
                fontSize: 11,
                color: P.teal,
                background: "rgba(30, 90, 107, .08)",
                border: `1px solid rgba(30, 90, 107, .25)`,
                padding: "4px 9px",
                borderRadius: 999,
                letterSpacing: ".04em",
              }}
            >
              {l}
            </span>
          ))}
        </div>

        {/* Toggle — show/hide full profile */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          style={{
            marginTop: 10,
            alignSelf: "flex-start",
            background: expanded ? P.cream : "transparent",
            color: P.teal,
            border: `1px solid ${P.teal}`,
            borderRadius: 999,
            padding: "8px 18px",
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: ".02em",
            cursor: "pointer",
            fontFamily: FONT,
            transition: "background .15s",
          }}
        >
          {expanded ? s.toggleLess[lang] : s.toggleMore[lang]}
        </button>

        {expanded && (
          <div
            style={{
              marginTop: 10,
              paddingTop: 18,
              borderTop: `1px solid ${P.line}`,
              display: "flex",
              flexDirection: "column",
              gap: 18,
            }}
          >
            <section>
              <h4 style={sectionHeading}>{s.bioHeading[lang]}</h4>
              <p style={paragraphStyle}>{coach.bio[lang]}</p>
            </section>
            <section>
              <h4 style={sectionHeading}>{s.approachHeading[lang]}</h4>
              <p style={paragraphStyle}>{coach.approach[lang]}</p>
            </section>
            <section>
              <h4 style={sectionHeading}>{s.credentialsHeading[lang]}</h4>
              <ul style={{ margin: 0, paddingLeft: 18, color: P.inkMuted, fontSize: 14.5, lineHeight: 1.55 }}>
                {coach.credentials[lang].map((c, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>{c}</li>
                ))}
              </ul>
            </section>

            {/* Quote */}
            <blockquote
              style={{
                margin: "6px 0 0",
                padding: "18px 22px",
                background: P.cream,
                borderLeft: `3px solid ${P.amber}`,
                borderRadius: "0 12px 12px 0",
                fontFamily: SERIF,
                fontStyle: "italic",
                color: P.tealDeep,
                fontSize: 16.5,
                lineHeight: 1.45,
              }}
            >
              «{coach.quote[lang]}»
            </blockquote>

            {/* CTA */}
            <a
              href={startHref(lang)}
              style={{
                alignSelf: "flex-start",
                background: P.teal,
                color: "#FFFFFF",
                textDecoration: "none",
                padding: "12px 22px",
                borderRadius: 999,
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: ".02em",
                fontFamily: FONT,
              }}
            >
              {s.cta[lang]} →
            </a>
          </div>
        )}
        {/* Статьи коуча в блоге (17.10.2026) — ссылка автора в статье ведёт сюда. */}
        {posts && posts.length > 0 && (
          <div style={{ fontFamily: FONT }}>
            <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 6 }}>{BLOG_COPY[lang].coachArticles}</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.5 }}>
              {posts.map((x) => (
                <li key={x.slug}>
                  <a href={blogPostPath(lang, x.slug)} style={{ color: P.teal }}>{x.title}</a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </article>
  );
}

const sectionHeading: React.CSSProperties = {
  margin: "0 0 8px",
  fontSize: 11,
  letterSpacing: ".22em",
  textTransform: "uppercase",
  fontWeight: 700,
  color: P.teal,
  fontFamily: FONT,
};

const paragraphStyle: React.CSSProperties = {
  margin: 0,
  color: P.inkMuted,
  fontSize: 14.5,
  lineHeight: 1.6,
};

export function CoachesPage({ lang, posts }: { lang: Lang; posts?: CoachPosts }) {
  const s = COACHES_PAGE_STRINGS;
  const sorted = [...COACHES].sort((a, b) => a.order - b.order);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: P.paper,
        backgroundImage:
          "radial-gradient(rgba(20,63,75,.04) 1px,transparent 1px),radial-gradient(rgba(20,63,75,.02) 1px,transparent 1px)",
        backgroundSize: "28px 28px,56px 56px",
        backgroundPosition: "0 0,14px 14px",
        fontFamily: FONT,
        color: P.ink,
      }}
    >
      {/* Общая шапка сайта, активный пункт — коучи */}
      <CareersNav lang={lang} active="coaches" />

      {/* Page header */}
      <header style={{ padding: "64px 0 32px" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto", padding: "0 24px" }}>
          <h1
            style={{
              fontFamily: SERIF,
              fontWeight: 400,
              fontSize: "clamp(28px, 4vw, 40px)",
              lineHeight: 1.15,
              letterSpacing: "-.01em",
              margin: 0,
              color: P.tealDeep,
            }}
          >
            {s.pageTitle[lang]}
          </h1>
          <p
            style={{
              maxWidth: 720,
              margin: "16px 0 0",
              fontSize: 16.5,
              lineHeight: 1.6,
              color: P.inkMuted,
            }}
          >
            {s.pageIntro[lang]}
          </p>
        </div>
      </header>

      {/* Coach grid — responsive 1→2→3 columns; one card looks correct
          because the grid uses minmax(min(420px, 100%), 1fr). */}
      <main style={{ padding: "0 0 80px" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto", padding: "0 24px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(min(420px, 100%), 1fr))",
              gap: 28,
            }}
          >
            {sorted.map((coach) => (
              <CoachCard key={coach.id} coach={coach} lang={lang} posts={posts?.[coach.id]} />
            ))}
          </div>
        </div>
      </main>

      <CareersFooter lang={lang} active="coaches" />
    </div>
  );
}
